import React, { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import InputAdornment from "@mui/material/InputAdornment";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import SearchIcon from "@mui/icons-material/Search";
import InputBase from "@mui/material/InputBase";
import { styled } from "@mui/material/styles";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import {
  SelectChangeEvent,
  Grid,
  Autocomplete,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
} from "@mui/material";
import Stack from "@mui/material/Stack";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs, { Dayjs } from "dayjs";
import axios from "axios";

// utils
import { isFormNotComplete, isFormNotError } from "../../../../utils";

// local component
import { APIEndpoint } from "../../../../client/apiconfig";
import {
  District,
  Prefix,
  Tambol,
  Gender,
  Province,
  Patient,
  InitialPatientProfile,
  InitialPatientAddress,
  PrefixPatient,
} from "../../../../intefaces/VerifyCendential";
import { set } from "react-hook-form";
import { BasicAuthUser } from "../../../../client/basicAuth";

const Search = styled("div")(({ theme }) => ({
  position: "relative",
  backgroundColor: "#ffffff",
  borderRadius: "20px",
  border: "1px solid #E6E6E6",
  width: "60%",
  [theme.breakpoints.up("sm")]: {
    marginLeft: theme.spacing(0),
    width: "60ch",
  },
}));

const SearchIconWrapper = styled("div")(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: "100%",
  position: "absolute",
  right: 0,
  pointerEvents: "none",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  color: "#B1B1B1",
}));

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: "inherit",
  width: "100%",
  "& .MuiInputBase-input": {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(0)})`,
    paddingRight: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create("width"),
    [theme.breakpoints.up("sm")]: {
      width: "100ch",
      "&:focus": {
        width: "100ch",
      },
    },
  },
}));

const CustomErrorHelperText = {
  fontWeight: 400,
  fontSize: "0.75rem",
  lineHeight: 1.66,
  textAlign: "left" as const,
  marginTop: "3px",
  marginRight: "14px",
  marginBottom: "0",
  marginLeft: "14px",
  color: "#d32f2f",
};

interface Step2Props {
  onValueChange: (value: boolean, objData?: any) => void;
}

export const Step2 = ({ onValueChange }: Step2Props) => {
  const [formData, setFormData] = useState<Patient>({
    profile: InitialPatientProfile(),
    address: InitialPatientAddress(),
  });

  const [gender, setGender] = useState<Gender[]>([]);
  const [prefix, setPrefix] = useState<PrefixPatient[]>([]);

  const [provinceAddress, setProvinceAddress] = useState<Province[]>([]);
  const [districts, setDistricts] = useState<District[]>([]);
  const [tambols, setTambols] = useState<Tambol[]>([]);
  const [provinceCode, setProvinceCode] = useState<string>("");
  const [districtCode, setDistrictCode] = useState<string>("");

  const [selectedBirthDate, setSelectedBdate] = useState<Dayjs | null>(null);
  const [selectPatientDate, setSelectPatientDate] = useState<Dayjs | null>(
    null
  );

  // for get post code
  const [selectedTambol, setSelectedTambol] = useState<Tambol | null>(null);

  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  // const username = process.env.REACT_APP_BASIC_AUTH_USERNAME;
  // const password = process.env.REACT_APP_BASIC_AUTH_PASSWORD;

  // if (!username || !password) {
  //   throw new Error("Basic Auth env variables are missing");
  // }

  // const BasicAuthUser = {
  //   username,
  //   password,
  // };

  const handleFormChange = (e: any) => {
    const { name, value } = e.target;
    const [section, field] = name.split(".") as [keyof Patient, string];
    try {
      setFormData((prevFormData) => {
        if (section === "profile" || section === "address") {
          return {
            ...prevFormData,
            [section]: {
              ...prevFormData[section],
              [field]: value,
            },
          };
        }
        return prevFormData; // Return unchanged if section is neither profile nor address
      });
      validateField(name, value);
    } catch (error) {
      console.log(error);
    }
  };

  const handleBirthDateChange = (newValue: any) => {
    setSelectedBdate(newValue);
    if (newValue) {
      try {
        setFormData((prevFormData) => {
          const updatedFormData = {
            ...prevFormData,
            profile: {
              ...prevFormData.profile,
              birthdate: newValue.format("YYYY-MM-DD"),
            },
          };
          // save to local storage
          // sessionStorage.setItem("patientForm", JSON.stringify(updatedFormData));
          return updatedFormData;
        });
        validateField("birthdate", newValue);
      } catch (error) {
        console.log(error);
      }
    }
  };

  const handlePatientDateChange = (newValue: any) => {
    setSelectPatientDate(newValue);
    if (newValue) {
      try {
        const updatedPatientDate = {
          date: newValue.format("YYYY-MM-DD"),
        };
        // sessionStorage.setItem("patientDate", JSON.stringify(updatedPatientDate)); // save to local storage
        validateField("date", newValue);
      } catch (error) {
        console.log(error);
      }
    }
  };

  const validateField = (name: string, value?: any) => {
    // console.log("name: ", name, " value: ", value);
    // declare variable for error message, section and field
    let error = "";
    let section = "";
    let field = "";

    if (!name.includes(".")) {
      // for case of no section example: cid, date, birthdate
      field = name;
    } else {
      // for case of section example: profile.cid, address.province
      [section, field] = name.split(".");
    }

    if (!value && parseInt(value) !== 0 && value !== "YYYY-MM-DD") {
      error = "กรุณาระบุข้อมูล";
    } else {
      switch (field) {
        case "cid":
          if (value.length !== 13) {
            error = "ระบุเลข 13 หลัก";
          }
          break;
        case "date":
          if (!dayjs(value).isValid()) {
            error = "กรุณาระบุข้อมูล";
          }
          break;
        case "birthdate":
          if (!dayjs(value).isValid()) {
            error = "กรุณาระบุข้อมูล";
          }
          break;
        case "age":
          if (parseFloat(value) <= 0) {
            error = "กรุณาระบุข้อมูลให้ถูกต้อง";
          } else if (!value) {
            error = "กรุณาระบุข้อมูล";
          }
          break;
        case "weight":
          if (parseFloat(value) <= 0) {
            error = "กรุณาระบุข้อมูลให้ถูกต้อง";
          } else if (!value) {
            error = "กรุณาระบุข้อมูล";
          }
          break;
        case "height":
          if (parseFloat(value) <= 0) {
            error = "กรุณาระบุข้อมูลให้ถูกต้อง";
          } else if (!value) {
            error = "กรุณาระบุข้อมูล";
          }
          break;
        case "blood_pressure":
          if (!value) {
            error = "กรุณาระบุข้อมูล";
          }
          break;
        case "pulse":
          if (!value) {
            error = "กรุณาระบุข้อมูล";
          } else if (parseFloat(value) <= 0) {
            error = "กรุณาระบุข้อมูลให้ถูกต้อง";
          }
          break;
        case "email":
          if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            error = "กรุณาระบุอีเมลให้ถูกต้อง";
          }
          break;
        case "phone":
          if (value.length !== 10) {
            error = "กรุณาระบุเลข 10 หลัก";
          }
          break;
        default:
          break;
      }
    }
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: error,
    }));
    // console.error("errors: ", errors.date);
  };



  const loadFormDataFromLocalStorage = async () => {
    const savedForm = sessionStorage.getItem("patientForm");
    if (savedForm) {
      const parsedForm = JSON.parse(savedForm);
      if (parsedForm.profile && parsedForm.profile.birthdate) {
        setSelectedBdate(dayjs(parsedForm.profile.birthdate)); // Convert the birthdate string to a Day.js object
      }
      // check if form data have province
      if (parsedForm.address.province) {
        // set province code here
        setProvinceCode(parsedForm.address.province);

        // get district data from localStorage
        const savedDistrict = sessionStorage.getItem("district");
        if (savedDistrict) {
          const parsedDistrict = JSON.parse(savedDistrict);
          setDistricts(parsedDistrict);
        }
      }

      // check if form data have district
      if (parsedForm.address.district) {
        // get tambol data from localStorage
        const savedTambol = sessionStorage.getItem("tambol");
        if (savedTambol) {
          const parsedTambol = JSON.parse(savedTambol);
          setTambols(parsedTambol);
        }
      }
      setFormData(parsedForm);
    }
  };

  const loadPatientDate = () => {
    const savedPatientDate = sessionStorage.getItem("patientDate");
    // console.log("savedPatientDate:", savedPatientDate);
    // console.log("typeof savedPatientDate:", typeof savedPatientDate);
    if (savedPatientDate) {
      const parsedPatientDate = JSON.parse(savedPatientDate);
      // console.log("parsedPatientDate.date:", parsedPatientDate);
      if (parsedPatientDate.date) {
        // Convert the birthdate string to a Day.js object and set to birthdate state
        setSelectPatientDate(dayjs(parsedPatientDate.date));
      }
    }
  };

  const fetchGender = async () => {
    try {
      const response = await axios.get(APIEndpoint.Gender, {
        auth: BasicAuthUser,
      });
      if (response.data) {
        setGender(response.data.result);
      } else {
        throw new Error("No data found in fetching gender response");
      }
    } catch (error) {
      console.error("Error fetching gender data: ", error);
    }
  };

  const fetchPrefix = async () => {
    try {
      // const responsePrefix = await axios.get(APIEndpoint.Prefix, {
      //   auth: BasicAuthUser,
      // });
      // if (responsePrefix.data) {
      //   const prefixData = responsePrefix.data;
      //   setPrefix(prefixData.result);
      // }

      const resPrefixPatient = await axios.get(APIEndpoint.PrefixPatient, {
        auth: BasicAuthUser,
      });
      if (resPrefixPatient.data) {
        // console.log("resPrefixPatient: ", resPrefixPatient);
        setPrefix(resPrefixPatient.data.result);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAddress = async () => {
    try {
      const res = await axios.get(APIEndpoint.Province, {
        auth: BasicAuthUser,
      });
      if (res.data) {
        const addressData = res.data;
        setProvinceAddress(addressData.result);
      }
    } catch (error) {
      console.error("Error fetching address data: ", error);
    }
  };

  const loadDataFromLocalStorage = async () => {
    loadPatientDate();
    // loadPatientInfo();
    loadFormDataFromLocalStorage();
  };

  const fetchDistrcit = async (code: string) => {
    if (code) {
      try {
        const response = await axios.get(`${APIEndpoint.District}`, {
          auth: BasicAuthUser,
          params: {
            province_code: code,
          },
        });
        if (response.data) {
          const data = response.data.result;
          setDistricts(data);
          // save data to local storage reduce server load
          sessionStorage.setItem("district", JSON.stringify(data));
        }
      } catch (error) {
        console.error("fetch district error: ", error);
      }
    }
  };

  const fetchTambol = async (code: string, pvCode?: string) => {
    // console.log("pvCode: ", pvCode);
    // console.log("province code: ", provinceCode);
    if (code) {
      try {
        const response = await axios.get(`${APIEndpoint.Tambol}`, {
          auth: BasicAuthUser,
          params: {
            province_code: provinceCode || pvCode,
            district_code: code,
          },
        });
        if (response.data) {
          // console.log("fetchTambol response: ", response);
          // * Reset Tambol
          // setFormData((prevFormData) => {
          //   const updatedFormData = {
          //     ...prevFormData,
          //     address: {
          //       ...prevFormData.address,
          //       subdistrict: "",
          //     },
          //   };
          //   return updatedFormData;
          // });
          // setTambols([]);

          const data = response.data.result;
          setTambols(data);
          sessionStorage.setItem("tambol", JSON.stringify(data)); // save data to local storage reduce server load
        } else {
          // console.log("fetchTambol no data: ", response);
        }
      } catch (error) {
        console.error(error);
      }
    }
  };

  const setPostCode = (postCode: string) => {
    setFormData((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        address: {
          ...prevFormData.address,
          zip_code: postCode,
        },
      };
      return updatedFormData;
    });
  };

  const clearDistrictData = () => {
    setDistricts([]);
    setTambols([]);
    setFormData((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        address: {
          ...prevFormData.address,
          district: "",
          subdistrict: "",
          zip_code: "",
        },
      };
      return updatedFormData;
    });
  };

  const clearTambolData = () => {
    setTambols([]);
    setFormData((prevFormData) => {
      const updatedFormData = {
        ...prevFormData,
        address: {
          ...prevFormData.address,
          subdistrict: "",
          zip_code: "",
        },
      };
      return updatedFormData;
    });
  };

  const checkDocUpdateMode = () => {
    // update flow will override create flow
    const getDocMode = sessionStorage.getItem("docMode");
    if (getDocMode === "edit") {
      // get data from localStorage
      const docData = sessionStorage.getItem("document");
      if (docData) {
        const parseData = JSON.parse(docData);
        // console.log("docData: ", parseData);
        const { json_description } = parseData;
        const { patient } = json_description;
        const { profile, address } = patient;

        // set data to form
        setFormData({
          profile: profile,
          address: address,
        });

        // console.log("Address Data: ", address);

        // make hn by split by "-"
        const hn = profile.hn.split("-");
        setFormData((prevFormData) => {
          const updatedFormData = {
            ...prevFormData,
            profile: {
              ...prevFormData.profile,
              hn: hn[1],
            },
          };
          return updatedFormData;
        });

        // set birthdate
        setSelectedBdate(dayjs(profile.birthdate));

        // set patient date
        setSelectPatientDate(dayjs(json_description.visit_date));

        // set province code
        setProvinceCode(address.province);

        // fetch district
        fetchDistrcit(address.province);

        // fetch tambol
        fetchTambol(address.district, address.province);
      }
    } else {
      // create flow
      loadDataFromLocalStorage();
    }
  };

  useEffect(() => {
    // override create flow
    checkDocUpdateMode();

    fetchGender();
    fetchPrefix();
    fetchAddress();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // console.log(
    //   "Patient date: ",
    //   isFormNotComplete(selectPatientDate?.format("YYYY-MM-DD"))
    // );
    // * When form data has change, Update state to inform parent component
    if (
      isFormNotComplete(formData) ||
      isFormNotComplete(selectPatientDate?.format("YYYY-MM-DD"))
    ) {
      onValueChange(true);
    } else {
      // make sure all fields are valid by send errors to isFormNotError
      if (isFormNotError(errors)) {
        onValueChange(true);
      } else {
        const localData = [];
        localData.push({ data: formData, key: "patientForm" });
        localData.push({
          data: { date: selectPatientDate?.format("YYYY-MM-DD") },
          key: "patientDate",
        });
        // localData.push({ data: formUserInfo, key: "patientFormInfo" });
        onValueChange(false, localData);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors, formData, selectPatientDate]);

  return (
    <React.Fragment>
      <Typography variant="h5" sx={{ mb: 4 }}>
        ข้อมูลผู้เข้ารับการรักษา
      </Typography>
      <form>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <Typography sx={{ mb: 2, fontWeight: "bold" }}>
            ค้นหาเลขประจำตัวประชาชน
          </Typography>
        </Box>
        <Box
          display="flex"
          alignItems="center"
          flexDirection={{ xs: "row", sm: "row", md: "row", lg: "row" }}
        >
          <Search
            sx={{
              flexGrow: 1,
              fontFamily: "Sarabun, sans-serif",
              width: { xs: "350px", sm: "100px", md: "350px", lg: "400px" }, // กำหนดความกว้างตามขนาดหน้าจอ
            }}
          >
            <SearchIconWrapper>
              <SearchIcon />
            </SearchIconWrapper>
            <StyledInputBase
              name="search"
              placeholder="ค้นหาเลขบัตรประจำตัวประชาชนผู้เข้ารับการรักษา"
              inputProps={{ "aria-label": "search" }}
            />
          </Search>
          <Button
            variant="contained"
            sx={{
              ml: { xs: 3, sm: 3, md: 3, lg: 3 },
              width: { xs: "90px", sm: "90px", md: "90px", lg: "90px" },
              backgroundColor: "#2F4491",
              color: "#fff",
              borderRadius: "20px",
              fontFamily: "Sarabun, sans-serif",
              "&:hover": {
                backgroundColor: "#2F4491",
              },
            }}
          >
            ค้นหา
          </Button>
        </Box>
        <Box sx={{ mt: 5, display: "flex", alignItems: "center" }}>
          <Typography sx={{ mr: 2, color: "#5B5B5B" }}>
            ข้อมูลผู้ป่วย
          </Typography>
          <Divider sx={{ flexGrow: 1, borderColor: "#98999B" }} />
        </Box>
        <Box component="form" sx={{ mt: 3 }}>
          <FormControl>
            <FormLabel id="demo-row-radio-buttons-group-label"></FormLabel>
            <RadioGroup
              row
              aria-labelledby="demo-row-radio-buttons-group-label"
              name="row-radio-buttons-group"
              defaultValue="เลขประจำตัวประชาชน"
            >
              <FormControlLabel
                value="เลขประจำตัวประชาชน"
                control={<Radio />}
                label="เลขประจำตัวประชาชน"
              />
              <FormControlLabel
                value="เลขหนังสือเดินทาง"
                control={<Radio />}
                label="เลขหนังสือเดินทาง"
              />
            </RadioGroup>
          </FormControl>
        </Box>
        <Box
          component="form"
          display="flex"
          flexDirection="row"
          gap={2}
          sx={{ my: 5, mb: 3, mt: 3 }}
        >
          <TextField
            name="profile.cid"
            label="เลขประจำตัวประชาชน/หนังสือเดินทาง"
            variant="outlined"
            fullWidth
            value={formData.profile.cid}
            onChange={handleFormChange}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
            error={!!errors.cid}
            helperText={errors.cid}
            inputProps={{ maxLength: 13, minLength: 13 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <CreditCardIcon />
                </InputAdornment>
              ),
            }}
            placeholder="เลขประจำตัวประชาชน/หนังสือเดินทาง"
            sx={{
              flexGrow: 1,
            }}
          />
          <Box flexDirection={"column"}>
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                label="วันที่เข้ารับการรักษา"
                value={selectPatientDate}
                onChange={handlePatientDateChange}
                format="YYYY-MM-DD"
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error: !!errors.date,
                    helperText: errors.date,
                    onBlur: (e) => {
                      validateField("date", e.target.value);
                    },
                  },
                }}
              />
              {/* <div style={CustomErrorHelperText}>กรุณาระบุข้อมูล</div> */}
            </LocalizationProvider>
          </Box>
        </Box>
        <Box component="form" sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={3}>
              <FormControl fullWidth variant="outlined" sx={{ width: "100%" }}>
                <Autocomplete
                  id="prefix-autocomplete"
                  options={prefix} // The array of options
                  getOptionLabel={(option) => option.prefix_name_th} // Specify which field to display
                  value={
                    formData.profile.prefix
                      ? prefix.find(
                        (option) =>
                          option.prefix_name_th === formData.profile.prefix
                      )
                      : null
                  }
                  onChange={(event, newValue) => {
                    handleFormChange({
                      target: {
                        name: "profile.prefix",
                        value: newValue ? newValue.prefix_name_th : "",
                      },
                    });
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="คำนำหน้าชื่อ"
                      variant="outlined"
                      fullWidth
                      onBlur={(e) =>
                        validateField("profile.prefix", e.target.value)
                      }
                      error={!!errors.prefix}
                      helperText={errors.prefix}
                    />
                  )}
                  isOptionEqualToValue={(option, value) =>
                    option.prefix_name_th === value?.prefix_name_th
                  } // Updated comparison
                />
              </FormControl>
            </Grid>
            {/* ชื่อ */}
            <Grid item xs={12} sm={4.5}>
              <TextField
                label="ชื่อ"
                variant="outlined"
                fullWidth
                name="profile.first_name"
                value={formData.profile.first_name}
                onChange={handleFormChange}
                onBlur={(e) => validateField(e.target.name, e.target.value)}
                error={!!errors.first_name}
                helperText={errors.first_name}
                sx={{
                  flexGrow: 1,
                }}
              />
            </Grid>
            {/* นามสกุล */}
            <Grid item xs={12} sm={4.5}>
              <TextField
                label="นามสกุล"
                variant="outlined"
                fullWidth
                name="profile.last_name"
                value={formData.profile.last_name}
                onChange={handleFormChange}
                onBlur={(e) => validateField(e.target.name, e.target.value)}
                error={!!errors.last_name}
                helperText={errors.last_name}
                sx={{
                  flexGrow: 1,
                }}
              />
            </Grid>
          </Grid>
        </Box>

        <Box component="form" sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Grid container spacing={2}>
                {/* เพศ */}
                <Grid item xs={6}>
                  <FormControl fullWidth variant="outlined">
                    <InputLabel>เพศ</InputLabel>
                    <Select
                      label="เพศ"
                      name="profile.gender"
                      value={formData.profile.gender}
                      onChange={handleFormChange}
                      onBlur={(e) =>
                        validateField(e.target.name, e.target.value)
                      }
                      error={!!errors.gender}
                    >
                      {gender &&
                        gender.length > 0 &&
                        gender.map((item) => (
                          <MenuItem value={item.gender_code}>
                            {item.gender_name_th}
                          </MenuItem>
                        ))}
                    </Select>
                    {errors.gender && (
                      <div style={CustomErrorHelperText}>{errors.gender}</div>
                    )}
                  </FormControl>
                </Grid>

                {/* วัน/เดือน/ปีเกิด */}
                <Grid item xs={6}>
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      label="วันเกิด"
                      value={selectedBirthDate}
                      onChange={handleBirthDateChange}
                      format="YYYY-MM-DD"
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          error: !!errors.birthdate,
                          helperText: errors.birthdate,
                          onBlur: (e) => {
                            validateField("birthdate", e.target.value);
                          },
                        },
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} sm={6}>
              <Grid container spacing={2}>
                {/* อายุ */}
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="อายุ"
                    variant="outlined"
                    name="profile.age"
                    value={formData.profile.age}
                    onChange={handleFormChange}
                    onBlur={(e) => validateField(e.target.name, e.target.value)}
                    error={!!errors.age}
                    helperText={errors.age}
                    type="number"
                    inputProps={{
                      min: 0,
                    }}
                    sx={{
                      flexGrow: 1,
                    }}
                  />
                </Grid>

                {/* HN */}
                <Grid item xs={6}>
                  <TextField
                    fullWidth
                    label="HN"
                    variant="outlined"
                    name="profile.hn"
                    value={formData.profile.hn}
                    onChange={handleFormChange}
                    onBlur={(e) => validateField(e.target.name, e.target.value)}
                    error={!!errors.hn}
                    helperText={errors.hn}
                    inputProps={{
                      type: "number",
                      min: 0,
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <Box component="form" sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} display={{ xs: "none", md: "block" }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    name="profile.weight"
                    label="น้ำหนัก (กก.)"
                    variant="outlined"
                    fullWidth
                    value={formData.profile.weight}
                    onChange={handleFormChange}
                    onBlur={(e) => validateField(e.target.name, e.target.value)}
                    error={!!errors.weight}
                    helperText={errors.weight}
                    type="number"
                    inputProps={{
                      min: 0,
                    }}
                    sx={{}}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    name="profile.height"
                    label="ส่วนสูง (ซม.)"
                    variant="outlined"
                    fullWidth
                    value={formData.profile.height}
                    onChange={handleFormChange}
                    onBlur={(e) => validateField(e.target.name, e.target.value)}
                    error={!!errors.height}
                    helperText={errors.height}
                    type="number"
                    inputProps={{
                      min: 0,
                    }}
                  />
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={6} display={{ xs: "none", md: "block" }}>
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    name="profile.blood_pressure"
                    label="ความดันโลหิต (มม.ปรอท)"
                    variant="outlined"
                    fullWidth
                    value={formData.profile.blood_pressure}
                    onChange={handleFormChange}
                    onBlur={(e) => validateField(e.target.name, e.target.value)}
                    error={!!errors.blood_pressure}
                    helperText={errors.blood_pressure}
                    type="string"
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    name="profile.pulse"
                    label="ชีพจร (ครั้ง/นาที)"
                    variant="outlined"
                    fullWidth
                    value={formData.profile.pulse}
                    onChange={handleFormChange}
                    onBlur={(e) => validateField(e.target.name, e.target.value)}
                    error={!!errors.pulse}
                    helperText={errors.pulse}
                    type="number"
                    inputProps={{ min: 0 }}
                  />
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", mt: 5, mb: 5 }}>
          <Typography sx={{ mr: 2, color: "#5B5B5B" }}>
            ข้อมูลที่สามารถติดต่อได้
          </Typography>
          <Divider sx={{ flexGrow: 1, borderColor: "#98999B" }} />
        </Box>
        <Box
          component="form"
          display="flex"
          flexDirection="row"
          gap={2}
          sx={{ mt: 3 }}
        >
          <TextField
            name="address.no"
            label="บ้านเลขที่ / เลขที่อาคาร"
            variant="outlined"
            value={formData.address.no}
            onChange={handleFormChange}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
            error={!!errors.no}
            helperText={errors.no}
            fullWidth
            type="text"
            placeholder="เช่น 109"
            sx={{
              flexGrow: 1,
            }}
          />
          <TextField
            name="address.village_no"
            label="หมู่ที่"
            variant="outlined"
            onChange={handleFormChange}
            value={formData.address.village_no}
            fullWidth
            type="number"
            placeholder="เช่น 13"
            sx={{
              flexGrow: 1,
            }}
          />
        </Box>
        <Box
          component="form"
          display="flex"
          flexDirection="row"
          gap={2}
          sx={{ mt: 3 }}
        >
          <TextField
            name="address.alley"
            label="ตรอก / ซอย"
            variant="outlined"
            onChange={handleFormChange}
            value={formData.address.alley}
            fullWidth
            sx={{
              flexGrow: 1,
            }}
          />
          <TextField
            name="address.street"
            label="ถนน"
            variant="outlined"
            fullWidth
            onChange={handleFormChange}
            value={formData.address.street}
            sx={{
              flexGrow: 1,
            }}
          />
        </Box>
        <Box component="form" sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6} lg={3}>
              <FormControl fullWidth variant="outlined">
                <Autocomplete
                  id="province"
                  options={provinceAddress} // The array of provinces
                  getOptionLabel={(item: Province) => item.province_thai_name} // Use 'province_thai_name' to display each option
                  value={
                    formData.address.province // Access 'province' in formData
                      ? provinceAddress.find(
                        (item) =>
                          item.province_code === formData.address.province
                      )
                      : null
                  }
                  onChange={(_, newValue) => {
                    handleFormChange({
                      target: {
                        name: "address.province", // Store the value in 'address.province'
                        value: newValue ? newValue.province_code : "", // Set the value to 'province_thai_name'
                      },
                    });
                    // set province code here
                    setProvinceCode(newValue?.province_code ?? "");
                    // reset district and tambol
                    clearDistrictData();
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="จังหวัด"
                      variant="outlined"
                      fullWidth
                      sx={{ width: "100%" }}
                      onBlur={(e) => {
                        fetchDistrcit(provinceCode);
                        validateField("address.province", e.target.value);
                      }}
                      error={!!errors.province}
                      helperText={errors.province}
                    />
                  )}
                  isOptionEqualToValue={
                    (province, value) =>
                      province.province_code === value?.province_code // Ensure correct comparison
                  }
                />
              </FormControl>
            </Grid>

            {/* Make Autocomplete for district */}
            <Grid item xs={12} md={6} lg={3}>
              <FormControl fullWidth variant="outlined">
                <Autocomplete
                  id="district"
                  options={districts} // The array of districts
                  getOptionLabel={(item: District) => item.district_thai_name} // Use 'district_thai_name' to display each option
                  value={
                    formData.address.district // Access 'district' in formData
                      ? districts.find(
                        (item) =>
                          item.district_code === formData.address.district
                      )
                      : null
                  }
                  onChange={(_, newValue) => {
                    handleFormChange({
                      target: {
                        name: "address.district", // Store the value in 'address.district'
                        value: newValue ? newValue.district_code : "", // Set the value to 'district_thai_name'
                      },
                    });
                    // set district code here
                    setDistrictCode(newValue?.district_code ?? "");
                    // reset tambol
                    clearTambolData();
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="อำเภอ"
                      variant="outlined"
                      fullWidth
                      sx={{ width: "100%" }}
                      onBlur={(e) => {
                        fetchTambol(districtCode);
                        validateField("address.district", e.target.value);
                      }}
                      error={!!errors.district}
                      helperText={errors.district}
                    />
                  )}
                  isOptionEqualToValue={
                    (district, value) =>
                      district.district_code === value?.district_code // Ensure correct comparison
                  }
                ></Autocomplete>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <FormControl fullWidth variant="outlined">
                <Autocomplete
                  id="tambol"
                  options={tambols} // The array of tambols
                  getOptionLabel={(item: Tambol) => item.tambon_thai_name} // Use 'tambon_thai_name' to display each option
                  value={
                    formData.address.subdistrict // Access 'subdistrict' in formData
                      ? tambols.find(
                        (item) =>
                          item.zip_code === formData.address.subdistrict
                      )
                      : null
                  }
                  onChange={(_, newValue) => {
                    handleFormChange({
                      target: {
                        name: "address.subdistrict", // Store the value in 'address.subdistrict'
                        value: newValue ? newValue.zip_code : "", // Set the value to 'tambon_thai_name'
                      },
                    });
                    // set postcode here
                    setSelectedTambol(newValue ?? null);
                    setPostCode(newValue?.zip_code ?? "");
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="ตำบล"
                      variant="outlined"
                      fullWidth
                      sx={{ width: "100%" }}
                      onBlur={(e) => {
                        validateField("address.subdistrict", e.target.value);
                      }}
                      error={!!errors.subdistrict}
                      helperText={errors.subdistrict}
                    />
                  )}
                  isOptionEqualToValue={
                    (tambol, value) => tambol.zip_code === value?.zip_code // Ensure correct comparison
                  }
                />
              </FormControl>
            </Grid>

            <Grid item xs={12} md={6} lg={3}>
              <TextField
                name="address.zip_code"
                label="รหัสไปรษณีย์"
                variant="outlined"
                value={formData.address.zip_code}
                onChange={handleFormChange}
                fullWidth
                disabled
              />
            </Grid>
          </Grid>
        </Box>
        <Box
          component="form"
          display="flex"
          flexDirection="row"
          gap={2}
          sx={{ mt: 4 }}
        >
          <TextField
            name="address.email"
            label="อีเมล"
            variant="outlined"
            type="email"
            fullWidth
            value={formData.address.email}
            onChange={handleFormChange}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
            error={!!errors.email}
            helperText={errors.email}
            sx={{
              flexGrow: 1,
            }}
          />
          <TextField
            label="เบอร์โทรศัพท์มือถือ"
            variant="outlined"
            fullWidth
            type="tel"
            name="address.phone"
            value={formData.address.phone}
            onChange={(e) => {
              if (/^\d*$/.test(e.target.value)) handleFormChange(e);
            }}
            onBlur={(e) => validateField(e.target.name, e.target.value)}
            error={!!errors.phone}
            helperText={errors.phone}
            sx={{
              flexGrow: 1,
            }}
            inputProps={{
              maxLength: 10,
              inputMode: "numeric",
              pattern: "[0-9]*",
            }}
          />
        </Box>
        <Box display="flex" justifyContent="flex-end" sx={{ mt: 2 }}>
          <Stack spacing={1} direction="row"></Stack>
        </Box>
      </form>
    </React.Fragment>
  );
};

export default Step2;
